import { useState } from "react";
import { IoClose } from "react-icons/io5";
import { FaFilePdf, FaTrash, FaCheckCircle, FaFileWord } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import { formatFileSize } from "../constant";
import styles from "../ResumePage.module.css";
import { axiosInstance } from "../../../utils/axios";

export const ResumeItem = ({
  file,
  isSelected,
  isUploading,
  progress,
  error,
  hasSearchResults,
  isSearching,
  onSelect,
  onRemove,
}) => {
  const [isDeleting, setIsDeleting] = useState(false);

  const handleClick = () => {
    if (!isUploading && !error && !isDeleting) {
      onSelect(file.id);
    }
  };

  const handleRemove = async (e) => {
    e.stopPropagation();

    if (isDeleting) return;

    // If upload failed, just remove locally (no API call needed)
    if (error || !file.docId) {
      onRemove(file.id);
      return;
    }

    setIsDeleting(true);

    try {
      const response = await axiosInstance.post('/search/delete_doc', {
        doc_id: file.docId || file.id
      });

      if (response.data.message === 'success') {
        onRemove(file.id);
      } else {
        setIsDeleting(false);
      }
    } catch (err) {
      setIsDeleting(false);
    }
  };

  // Status text
  let statusText = null;
  if (error) {
    statusText = <span className={styles.errorText}> - {error}</span>;
  } else if (isDeleting) {
    statusText = <span className={styles.searchingText}> - Deleting...</span>;
  } else if (isSearching) {
    statusText = <span className={styles.searchingText}> - Searching...</span>;
  } else if (hasSearchResults) {
    statusText = <span className={styles.cachedText}> - Ready</span>;
  }

  // Status icon
  let statusIcon = null;
  if (isUploading) {
    statusIcon = <AiOutlineLoading3Quarters className={styles.loadingIcon} />;
  } else if (error) {
    statusIcon = <IoClose className={styles.errorIcon} />;
  } else if (isDeleting) {
    statusIcon = <AiOutlineLoading3Quarters className={styles.loadingIcon} />;
  } else if (isSearching) {
    statusIcon = <AiOutlineLoading3Quarters className={styles.loadingIcon} />;
  } else if (hasSearchResults) {
    statusIcon = <FaCheckCircle className={styles.successIcon} />;
  }

  const getFileIcon = () => {
    const fileName = file.name.toLowerCase();
    const fileType = file.type;

    if (fileType === "application/pdf" || fileName.endsWith('.pdf')) {
      return <FaFilePdf className={styles.fileIconPdf} />;
    } else if (
      fileType === "application/msword" ||
      fileType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document" ||
      fileName.endsWith('.doc') ||
      fileName.endsWith('.docx')
    ) {
      return <FaFileWord className={styles.fileIconWord} />;
    }
    return <FaFilePdf className={styles.fileIconPdf} />;
  };

  return (
    <div
      className={`${styles.fileItem} ${isSelected ? styles.fileItemSelected : ""} ${error ? styles.fileItemError : ""} ${isSearching || isDeleting ? styles.fileItemSearching : ""}`.trim()}
      onClick={handleClick}
    >
      <div className={styles.fileItemContent}>
        <div className={styles.fileIconWrapper}>
          {getFileIcon()}
        </div>

        <div className={styles.fileItemDetails}>
          <p className={styles.fileItemName}>
            <span className={styles.fileName}>{file.name}</span>
          </p>
          <p className={styles.fileItemSize}>
            {file.isServerFile && file.size === 0 ? "Saved" : formatFileSize(file.size)}
            {statusText}
          </p>
        </div>

        <div className={styles.statusIconsContainer}>
          {statusIcon}
          {!isUploading && !isDeleting && (
            <button
              className={styles.removeFileButton}
              onClick={handleRemove}
              disabled={isDeleting}
            >
              <FaTrash />
            </button>
          )}
        </div>
      </div>

      {isUploading && (
        <div className={styles.progressBar}>
          <div className={styles.progressFill} style={{ width: `${progress}%` }} />
        </div>
      )}

      {(isSearching || isDeleting) && (
        <div className={styles.searchProgressBar}>
          <div className={styles.searchProgressFill} />
        </div>
      )}
    </div>
  );
};